import { useEffect, useLayoutEffect, useRef } from "react";
import type { ReactNode, RefObject } from "react";

/** Walks up to the nearest ancestor that actually clips/scrolls its
 * content. Clamping against `window` instead of this was the bug: a
 * popover could sit well inside the browser window (so a window-relative
 * check saw no problem) while still overflowing PAST the message pane's
 * own edge, behind the sidebar — which both looked cut off (the sidebar
 * paints over it) AND forced the pane's own scrollable area wider,
 * exactly the "have to scroll to see it" symptom.
 *
 * Falls back to `.main` (the message-pane column, to the right of the
 * sidebar) if no ancestor actually scrolls — a popover anchored to the
 * COMPOSER (the send-later menu, say) has no scrolling ancestor at all
 * since `.composer` sits outside `.msgs`, so without this it fell straight
 * through to the window-wide check and reproduced the exact bug this was
 * meant to fix, just for that one popover. */
function nearestScrollParent(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node) {
    const style = getComputedStyle(node);
    if (/(auto|scroll)/.test(style.overflowX + style.overflowY)) return node;
    node = node.parentElement;
  }
  return el.closest<HTMLElement>(".main");
}

/**
 * Shifts a popover back into view if it overflows any edge of its
 * containing scrollable pane (or the browser window, if it isn't inside
 * one) — both axes, not just left/right, since a popover that opens
 * downward can just as easily run off the BOTTOM of the screen depending
 * on where in a scrolled list you triggered it.
 *
 * Every popover in this app is CSS-anchored to one side of its trigger
 * (e.g. `right: 0`, growing leftward, or `top: 100%`, growing downward) on
 * the assumption there's room to grow into — true most of the time, but a
 * trigger sitting near an edge breaks that assumption and runs the
 * popover off-screen. Measuring the ACTUAL rendered position after mount
 * and nudging it back with a transform works regardless of which
 * direction the CSS anchored it in, so it doesn't matter which specific
 * rule caused the overflow, or which edge it happened on.
 */
export function clampToViewport(el: HTMLElement | null): void {
  if (!el) return;
  // Clear any correction from a PREVIOUS run before measuring, but keep
  // whatever transform the element's own CSS rule defines (e.g.
  // AvatarHoverCard centers its tooltip with `transform: translateX(-50%)`
  // — overwriting that outright, rather than composing with it, would
  // un-center the tooltip entirely the moment this hook ever nudges it).
  el.style.transform = "";
  const baseTransform = getComputedStyle(el).transform;
  const base = baseTransform && baseTransform !== "none" ? baseTransform : "";

  const margin = 8;
  const rect = el.getBoundingClientRect();
  const container = nearestScrollParent(el);
  const bounds = container
    ? container.getBoundingClientRect()
    : ({ left: 0, right: window.innerWidth, top: 0, bottom: window.innerHeight } as DOMRect);

  let dx = 0;
  if (rect.left < bounds.left + margin) dx = bounds.left + margin - rect.left;
  else if (rect.right > bounds.right - margin) dx = bounds.right - margin - rect.right;

  let dy = 0;
  if (rect.top < bounds.top + margin) dy = bounds.top + margin - rect.top;
  else if (rect.bottom > bounds.bottom - margin) dy = bounds.bottom - margin - rect.bottom;

  if (dx !== 0 || dy !== 0) {
    el.style.transform = base ? `${base} translate(${dx}px, ${dy}px)` : `translate(${dx}px, ${dy}px)`;
  }
}

export function useClampToViewport<T extends HTMLElement>(ref: RefObject<T>, deps: unknown[] = []) {
  useLayoutEffect(() => {
    clampToViewport(ref.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/** Positioning + dismissal wrapper for a trigger button and its popover.
 * The trigger lives INSIDE the anchor, so a pointerdown on it never counts
 * as "outside" — its own onClick toggles open/closed with no listener
 * race (the bug a document-level click handler used to cause here). */
export default function PopoverAnchor({
  active,
  onDismiss,
  children,
}: {
  active: boolean;
  onDismiss: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!active) return;
    const onPointerDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onDismiss();
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [active, onDismiss]);
  return (
    <span style={{ position: "relative", display: "inline-flex" }} ref={ref}>
      {children}
    </span>
  );
}
